import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnalyseIA from './AnalyseIA';
import { lignesDuBloc } from '../utils/lignesDuDashboard';
import { Users, AlertTriangle, BarChart3, TrendingUp } from 'lucide-react';
import DashboardLoadingScreen from './DashboardLoadingScreen';
import NoDataMessage from './NoDataMessage';
import awinLogo from '../logos/awin.png';
import PerformanceChart from './PerformanceChart';
import CampaignsTable from './CampaignsTable';
import AwinCommissionStatus from './AwinCommissionStatus';
import { useClientModuleVisibility } from '../hooks/useClientModuleVisibility';
import { synchroniserCampagnes } from '../utils/synchroCampagnes';
import { compteAutorise, ensembleAutorise } from '../utils/accountMatching';

/**
 * Dashboard Awin (affiliation), côté annonceur.
 *
 * Deux partis pris distinguent cet écran des autres dashboards de régie, et tous deux
 * découlent de ce qu'Awin sait dire :
 *
 * - **Le budget, c'est la commission engagée** — validée, en attente et primes. La part
 *   encore en attente est affichée à côté, parce qu'elle est massive sur une période
 *   récente et qu'elle expliquera l'essentiel des écarts d'un jour à l'autre.
 * - **Les clics ne sont pas ventilés par jour.** Awin agrège impressions et clics sur la
 *   période demandée, sans dimension de date. On affiche donc leur total sans tracer de
 *   courbe : inventer une répartition serait pire que de s'en passer.
 *
 * La section Résultats est rendue par `PerformanceChart`, le composant partagé par tous les
 * dashboards publicitaires : cartes, courbe, granularité, comparaison de période,
 * personnalisation des métriques et visibilité des modules en viennent d'un seul endroit. Ce
 * fichier ne garde donc que ce qu'aucun autre dashboard n'a : la ventilation par statut de
 * commission, et le classement des éditeurs.
 */

const API_URL = process.env.REACT_APP_API_URL || '';

const euros = (v) => (v == null
  ? '—'
  : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v));

const entier = (v) => (v == null ? '—' : new Intl.NumberFormat('fr-FR').format(Math.round(v)));

const pourcent = (v) => (v == null ? '—' : `${v.toFixed(1).replace('.', ',')} %`);

const AwinDashboardView = ({
  /**
   * L'analyse IA est-elle ouverte à cet utilisateur ?
   *
   * Un membre d'organisation est jugé sur son plan, un accès client sur la permission
   * `analyse-ia` accordée dans Organisation › Accès clients. La règle est calculée une fois
   * dans `App` et descendue ici : `autorise` valant `true` par défaut, ne rien passer montrait
   * le bouton à un client que le serveur refuse ensuite.
   */
  analyseAutorisee = false,
  token,
  selectedPeriod = 'LAST_30_DAYS',
  customStartDate,
  customEndDate,
  selectedClientId,
  selectedSiteId,
  userSites = [],
  preselectedAccountId,
  compareEnabled = false,
  comparisonType = 'previous_period',
  formatNumber,
  KPICard,
  dashboardNotes = [],
  userRole = 'USER'
}) => {
  const [comptes, setComptes] = useState([]);
  const [compteSelectionne, setCompteSelectionne] = useState(null);
  const [chargementComptes, setChargementComptes] = useState(true);
  const [comptesDuClient, setComptesDuClient] = useState(null);
  const [donnees, setDonnees] = useState(null);
  const [resultats, setResultats] = useState(null);
  /**
   * Campagne sur laquelle tout le dashboard est filtré, ou `null`.
   *
   * Le filtrage se fait **côté serveur** : toutes les données d'Awin découlent des mêmes
   * transactions, et le serveur les garde en mémoire une minute. Refiltrer ne coûte donc aucun
   * appel à Awin, et les cartes, la courbe, les éditeurs et la maturité des commissions restent
   * calculés au même endroit — ils ne peuvent pas diverger d'un filtre à l'autre.
   */
  const [campagneFiltre, setCampagneFiltre] = useState(null);

  // Changer de compte remet le filtre à zéro : une campagne d'un compte n'existe pas sur un
  // autre, et le tableau se retrouverait vide sans que rien ne l'explique.
  useEffect(() => { setCampagneFiltre(null); }, [compteSelectionne?.id]);
  const [periodePrecedente, setPeriodePrecedente] = useState(null);
  const [granularite, setGranularite] = useState('day');
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);

  const clientEffectif = useMemo(
    () => (compteSelectionne ? `awin_${compteSelectionne.sourceId || compteSelectionne.id}` : null),
    [compteSelectionne]
  );
  const { isModuleVisible, toggleModuleVisibility } = useClientModuleVisibility(clientEffectif, userRole);

  // --- Les comptes Awin de la bibliothèque
  useEffect(() => {
    let annule = false;

    const charger = async () => {
      setChargementComptes(true);
      try {
        const reponse = await fetch(`${API_URL}/api/library?usage=dashboards`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await reponse.json();
        if (annule) return;

        const items = data?.library?.AWIN || [];
        // Un compte rattaché à plusieurs sites peut remonter deux fois
        setComptes(items.filter((item, i, tous) => i === tous.findIndex(t => t.id === item.id)));
      } catch (e) {
        if (!annule) setErreur('Impossible de charger les comptes Awin');
      } finally {
        if (!annule) setChargementComptes(false);
      }
    };

    if (token) charger();
    return () => { annule = true; };
  }, [token]);

  /**
   * Les comptes rattachés aux sites du client sélectionné.
   *
   * `userSites` ne porte pas le client de chaque site de façon fiable selon le chemin
   * d'entrée : on interroge donc `/api/sites?clientId=`, comme les dashboards Google,
   * Microsoft, Meta, Pinterest, TikTok et ChatGPT Ads. Sans ce filtre, choisir un client
   * laissait visibles les comptes Awin de tous les autres.
   */
  useEffect(() => {
    let annule = false;

    const charger = async () => {
      if (!selectedClientId || !token) {
        setComptesDuClient(null);
        return;
      }
      try {
        const reponse = await fetch(`${API_URL}/api/sites?clientId=${encodeURIComponent(selectedClientId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await reponse.json();
        if (annule) return;
        setComptesDuClient(ensembleAutorise((data.sites || []).flatMap(site => site.accountIds || [])));
      } catch (e) {
        // Un filtre indisponible ne doit pas masquer les comptes
        if (!annule) setComptesDuClient(null);
      }
    };

    charger();
    return () => { annule = true; };
  }, [selectedClientId, token]);

  /**
   * Le site sélectionné prime sur le client : c'est le filtre le plus précis.
   *
   * ⚠️ Le rapprochement passe par `accountMatching` : `/api/library` expose `id` **égal au
   * sourceId** et range l'identifiant de base sous `libraryId`, alors qu'un rattachement
   * récent inscrit ce dernier (un cuid) dans `Site.accountIds`. Comparer à `id` et
   * `sourceId` seuls ne testait qu'une des identités, et le dashboard se disait vide.
   */
  const comptesVisibles = useMemo(() => {
    if (selectedSiteId && selectedSiteId !== 'all') {
      const site = userSites.find(s => (s._id || s.id) === selectedSiteId);
      if (site?.accountIds?.length) {
        const autorises = ensembleAutorise(site.accountIds);
        return comptes.filter(c => compteAutorise(c, autorises));
      }
      // Un site sans compte rattaché ne borne rien : on retombe sur le filtre client.
    }
    if (comptesDuClient) {
      return comptes.filter(c => compteAutorise(c, comptesDuClient));
    }
    return comptes;
  }, [comptes, selectedSiteId, userSites, comptesDuClient]);

  useEffect(() => {
    if (comptesVisibles.length === 0) { setCompteSelectionne(null); return; }
    const prechoisi = preselectedAccountId
      && comptesVisibles.find(c => c.id === preselectedAccountId || c.sourceId === preselectedAccountId);
    const courant = compteSelectionne && comptesVisibles.find(c => c.id === compteSelectionne.id);
    setCompteSelectionne(prechoisi || courant || comptesVisibles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comptesVisibles, preselectedAccountId]);

  /**
   * Second temps : la ventilation des clics par jour, une fois la page à l'écran.
   *
   * Awin ne datant pas son rapport de trafic, chaque journée coûte un appel — quatorze
   * suffisent à saturer le débit autorisé. Attendre ces appels avant d'afficher ferait
   * patienter une minute pour une courbe secondaire, alors que les commissions et les ventes
   * sont déjà là. Mesuré : soixante secondes dès qu'on enchaîne deux périodes.
   *
   * La courbe est enrichie **en place** : elle garde son échelle et sa granularité, une série
   * de plus y apparaît. Un échec ne dit rien à l'utilisateur — les trous se lisent comme
   * « non mesuré », ce qu'ils sont, et un bandeau d'erreur ferait douter de chiffres justes.
   *
   * Même patron que les visuels de Facebook Ads, chargés une fois le tableau affiché.
   */
  const chargerTraficQuotidien = useCallback(async (estAnnule) => {
    if (!compteSelectionne || !token) return;

    try {
      const reponse = await fetch(`${API_URL}/api/dashboard/awin/trafic-quotidien`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          accountId: compteSelectionne.sourceId || compteSelectionne.id,
          period: selectedPeriod,
          customStartDate,
          customEndDate
        })
      });
      const data = await reponse.json();
      if (estAnnule() || !data.success || Object.keys(data.parJour || {}).length === 0) return;

      setResultats(actuels => {
        if (!actuels) return actuels;
        const cle = Object.keys(actuels.rawData || {})[0];
        const brut = actuels.rawData[cle];
        if (!brut) return actuels;

        return {
          ...actuels,
          charts: (actuels.charts || []).map(c => ({
            ...c,
            metrics: [...new Set([...(c.metrics || []), 'clicks'])],
            data: (c.data || []).map(p => (
              data.parJour[p.date] ? { ...p, clicks: data.parJour[p.date].clicks } : p
            ))
          })),
          rawData: {
            ...actuels.rawData,
            [cle]: {
              ...brut,
              enrichedDailyData: brut.enrichedDailyData.map(j => (
                data.parJour[j.date] ? { ...j, clicks: data.parJour[j.date].clicks } : j
              ))
            }
          }
        };
      });
    } catch (e) {
      // Silence volontaire : voir ci-dessus.
    }
  }, [compteSelectionne, token, selectedPeriod, customStartDate, customEndDate]);

  // --- Les données du compte choisi
  useEffect(() => {
    let annule = false;

    const charger = async () => {
      if (!compteSelectionne || !token) return;

      setChargement(true);
      setErreur(null);
      try {
        const params = new URLSearchParams({ period: selectedPeriod });
        if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
          params.set('startDate', customStartDate);
          params.set('endDate', customEndDate);
        }
        if (campagneFiltre?.id) params.set('campagne', campagneFiltre.id);

        const reponse = await fetch(
          `${API_URL}/api/dashboard/awin/${compteSelectionne.id}?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await reponse.json();
        if (annule) return;

        if (!data.success) { setErreur(data.error); setDonnees(null); setResultats(null); return; }
        setDonnees(data);

        /**
         * Enregistrer les campagnes en base, comme le font les dashboards Google, Microsoft et Meta.
         *
         * Les labels et les objectifs sont attachés à une ligne `Campaign`, pas aux données de la
         * régie : sans cet appel, le panneau « Labels et objectifs » s'ouvrirait sur une table vide,
         * ce qui ressemble à une panne plutôt qu'à une absence de donnée.
         */
        // ⚠️ `awinCampagnes` et non `campaigns` : cette dernière clé porte les **éditeurs**,
        // qu'on n'étiquette pas.
        synchroniserCampagnes(
          token,
          compteSelectionne?.sourceId || compteSelectionne?.id,
          'AWIN',
          Object.values(data.rawData || {}).flatMap(c => c.awinCampagnes || [])
        );

        // Le composant « Résultats » attend `{ charts, rawData }` : c'est un second appel,
        // mais sur la même période et le même compte, donc les deux jeux concordent.
        const rTimeline = await fetch(`${API_URL}/api/dashboard/awin/timeline`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            sources: [compteSelectionne.sourceId || compteSelectionne.id],
            period: selectedPeriod,
            customStartDate,
            customEndDate,
            campagne: campagneFiltre?.id || null
          })
        });
        const timeline = await rTimeline.json();
        if (annule) return;
        setResultats(timeline.success ? timeline : null);

        // Puis, sans attendre, le trafic — voir `chargerTraficQuotidien`.
        if (timeline.success) chargerTraficQuotidien(() => annule);
      } catch (e) {
        if (!annule) { setErreur('Impossible de charger les données Awin'); setDonnees(null); }
      } finally {
        if (!annule) setChargement(false);
      }
    };

    charger();
    return () => { annule = true; };
  }, [compteSelectionne, token, selectedPeriod, customStartDate, customEndDate, campagneFiltre, chargerTraficQuotidien]);

  /**
   * La période de comparaison, quand elle est demandée.
   *
   * Ses bornes se déduisent de celles réellement interrogées, et non de la période nommée :
   * les deux durées doivent coïncider, sinon l'écart affiché mêle performance et nombre de
   * jours.
   */
  useEffect(() => {
    let annule = false;

    const charger = async () => {
      const periode = resultats && Object.values(resultats.rawData || {})[0]?.period;
      if (!compareEnabled || !compteSelectionne || !periode) {
        setPeriodePrecedente(null);
        return;
      }
      try {
        const reponse = await fetch(`${API_URL}/api/dashboard/awin/previous-period`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            accountId: compteSelectionne.sourceId || compteSelectionne.id,
            startDate: periode.startDate,
            endDate: periode.endDate,
            comparisonType
          })
        });
        const data = await reponse.json();
        if (annule) return;
        // Les totaux à plat : c'est ce que le composant compare, pas l'enveloppe.
        setPeriodePrecedente(data.success ? data.metrics : null);
      } catch (e) {
        if (!annule) setPeriodePrecedente(null);
      }
    };

    charger();
    return () => { annule = true; };
  }, [compareEnabled, compteSelectionne, resultats, token, comparisonType]);

  /**
   * Le jeu de données du tableau des campagnes.
   *
   * Le composant partagé lit `rawData[*].campaigns` ; cette clé porte déjà les éditeurs. On
   * recompose donc un objet de même forme dont `campaigns` vaut les campagnes Awin — le
   * tableau se comporte alors exactement comme les autres, sans qu'il ait à connaître Awin.
   *
   * Placé **avant les retours anticipés** : un hook appelé après un `return` conditionnel ne
   * s'exécuterait pas au même rang d'un rendu à l'autre, et React le refuse.
   */
  const rawDataCampagnes = useMemo(() => {
    const brut = resultats?.rawData;
    if (!brut) return null;
    const entrees = Object.entries(brut)
      .filter(([, v]) => Array.isArray(v?.awinCampagnes) && v.awinCampagnes.length > 0)
      .map(([cle, v]) => [cle, { ...v, campaigns: v.awinCampagnes }]);
    return entrees.length > 0 ? Object.fromEntries(entrees) : null;
  }, [resultats]);

  if (chargementComptes) return <DashboardLoadingScreen />;

  if (comptesVisibles.length === 0) {
    return <NoDataMessage message="Aucun compte Awin connecté pour cette sélection." />;
  }

  const t = donnees?.totaux;
  const partEnAttente = t && t.cost > 0 ? (t.coutDetail.pending / t.cost) * 100 : null;


  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Appelée à l'envoi de la question et non à l'ouverture du panneau : les blocs d'un dashboard
   * arrivent en plusieurs temps, et une charge figée trop tôt capture un écran incomplet.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    const bruts = {
      'Résultats': resultats,
      'Éditeurs': donnees?.editeurs || donnees?.publishers,
      'Commissions': donnees?.commissions,
      'Période précédente': periodePrecedente
    };
    const blocs = {};
    const absents = [];
    for (const [libelle, bloc] of Object.entries(bruts)) {
      const reduit = bloc ? lignesDuBloc(bloc) : null;
      if (reduit) blocs[libelle] = reduit;
      else absents.push(libelle);
    }

    return {
      dashboardData: {
        sources: [compteSelectionne?.sourceId || compteSelectionne?.id].filter(Boolean),
        ...periode,
        charts: resultats?.charts,
        composants: { blocs, absents },
        dashboardType: 'awin'
      },
      dashboardType: 'awin',
      accountId: compteSelectionne?.sourceId || compteSelectionne?.id || null,
      accountName: compteSelectionne?.name || null,
      ...periode
    };
  };

  return (
    <div className="step-content">
      {/*
        Pas de titre ni de logo ici : le fil d'Ariane les porte déjà, juste au-dessus. Les
        répéter ajoutait une ligne qui ne disait rien de neuf et éloignait les chiffres du haut
        de l'écran.

        Le sélecteur de compte reste, mais seulement quand il y a un choix à faire.
      */}
      {/* Filtre actif : sans ce bandeau, tout l'écran change sans qu'aucun élément ne dise
          pourquoi — et la disparition des clics passerait pour une panne. */}
      {campagneFiltre && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px',
          padding: '10px 14px', marginBottom: '16px', fontSize: '0.875rem', color: '#1e3a8a'
        }}>
          <span>
            Tout l'écran est filtré sur la campagne <strong>{campagneFiltre.name}</strong>.
          </span>
          <span style={{ color: '#3b82f6' }}>
            Les clics et le CPC ne s'affichent pas : Awin ventile son trafic par éditeur, jamais
            par campagne.
          </span>
          <button
            onClick={() => setCampagneFiltre(null)}
            style={{
              marginLeft: 'auto', padding: '4px 12px', borderRadius: '8px',
              border: '1px solid #93c5fd', background: 'white', color: '#1d4ed8',
              fontSize: '0.8125rem', cursor: 'pointer'
            }}
          >
            Retirer le filtre
          </button>
        </div>
      )}

      {comptesVisibles.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <select
            value={compteSelectionne?.id || ''}
            onChange={(e) => setCompteSelectionne(comptesVisibles.find(c => c.id === e.target.value))}
            style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }}
          >
            {comptesVisibles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {/*
        L'analyse IA, posée au-dessus du contenu plutôt que dans une barre de filtres : ce
        dashboard n'en a pas. Le composant rend le bouton *et* le panneau — celui-ci passant par
        un portail, sa place dans l'arbre n'a aucune incidence sur son placement à l'écran.
      */}
      {compteSelectionne && !chargement && donnees && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <AnalyseIA
            token={token}
            autorise={analyseAutorisee}
            compte={compteSelectionne}
            construireCharge={construireChargeAnalyse}
          />
        </div>
      )}

      {erreur && (
        <div style={{
          padding: '12px 16px', borderRadius: '10px', background: '#fef2f2',
          border: '1px solid #fecaca', color: '#991b1b', marginBottom: '20px'
        }}>
          {erreur}
        </div>
      )}

      {/* Le même écran que les autres dashboards : ce moment d'attente doit se ressembler
          d'une régie à l'autre, sinon chacune paraît se charger différemment. */}
      {compteSelectionne && chargement && (
        <DashboardLoadingScreen
          platformName="Awin"
          features={[
            { icon: <BarChart3 size={28} />, label: 'Commissions et ventes' },
            { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
            { icon: <img src={awinLogo} alt="Awin" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />, label: 'Performance des éditeurs' }
          ]}
        />
      )}

      {!chargement && donnees && (
        <>
          {/*
            La section Résultats vient du composant partagé : cartes, courbe, granularité,
            comparaison de période et choix des métriques y sont déjà résolus, et se
            comportent ici comme sur les autres dashboards.
          */}
          {resultats && (
            <PerformanceChart
              autoCharts={resultats}
              formatNumber={formatNumber}
              KPICard={KPICard}
              previousPeriodData={periodePrecedente}
              selectedCampaignFilter={null}
              selectedCampaignTypeFilter={null}
              selectedAccount={compteSelectionne}
              granularity={granularite}
              onGranularityChange={setGranularite}
              compareEnabled={compareEnabled}
              comparisonType={comparisonType}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              labelCampaignIds={null}
              notes={dashboardNotes}
              userRole={userRole}
              isClientModuleVisible={isModuleVisible}
              toggleClientModuleVisibility={toggleModuleVisibility}
              moduleId="awin_results"
            />
          )}

          {/*
            Ce qu'aucun autre dashboard n'a : la maturité des commissions et le nombre
            d'éditeurs. Ces deux mesures ne passent pas par le système de colonnes
            configurables, qui suppose des métriques additives — or un éditeur présent
            trois jours de suite en fait un, pas trois.
          */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
            padding: '12px 16px', borderRadius: '10px', background: 'white',
            border: '1px solid #e5e7eb', margin: '0 0 22px 0', fontSize: '13px'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={14} /><strong>{entier(t.editeursActifs)}</strong> éditeurs actifs
            </span>
            <span><strong>{euros(t.coutDetail.approved)}</strong> validé</span>
            <span><strong>{euros(t.coutDetail.pending)}</strong> en attente</span>
            {t.coutDetail.declined > 0 && (
              <span style={{ color: '#9ca3af' }}>{euros(t.coutDetail.declined)} annulé</span>
            )}
            {t.tauxValidation != null && (
              <span>Taux de validation <strong>{pourcent(t.tauxValidation)}</strong></span>
            )}
          </div>

          {/* La validation des commissions s'étale sur des semaines : le dire évite de
              prendre un mois récent pour un mois bon marché. */}
          {partEnAttente != null && partEnAttente > 25 && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '12px 16px', borderRadius: '10px', background: '#fffbeb',
              border: '1px solid #fde68a', color: '#92400e', marginBottom: '22px', fontSize: '13px'
            }}>
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>
                {pourcent(partEnAttente)} des commissions de cette période attendent encore leur
                validation par l'annonceur. Le montant définitif sera inférieur si des ventes
                sont annulées.
              </span>
            </div>
          )}

          {/*
            Le tableau des campagnes, alimenté par les éditeurs : c'est le même composant que
            sur les autres dashboards, donc la même charte, le même tri, la même recherche et
            la même configuration de colonnes — sans réécrire une imitation qui divergerait.
          */}
          {resultats && (
            <CampaignsTable
              rawData={resultats.rawData}
              formatNumber={formatNumber}
              selectedCampaignFilter={null}
              setSelectedCampaignFilter={() => {}}
              selectedCampaignTypeFilter={null}
              selectedAccount={compteSelectionne}
              compareEnabled={compareEnabled}
              comparisonType={comparisonType}
              userRole={userRole}
              isClientModuleVisible={isModuleVisible}
              toggleClientModuleVisibility={toggleModuleVisibility}
              moduleId="awin_publishers"
              titre="Éditeurs"
              titreColonne="Éditeur"
            />
          )}

          {/* Les campagnes Awin — « Aperçu des campagnes » dans son interface.
              Le tableau partagé lit `rawData[*].campaigns`, clé déjà prise par les éditeurs :
              on lui passe donc un jeu recomposé à partir de `awinCampagnes`, plutôt que
              d'ajouter au composant partagé un paramètre dont aucun autre dashboard n'a
              l'usage. */}
          {/* Seul ce tableau reçoit `campagnesAccountId` / `campagnesAccountType` : le tableau
              des éditeurs, plus haut, ne les reçoit pas — un éditeur n'est pas une campagne et
              ne s'étiquette pas. L'exception est portée ici, et non par une liste noire sur
              `moduleId` dans le composant partagé. */}
          {rawDataCampagnes && (
            <CampaignsTable
              rawData={rawDataCampagnes}
              formatNumber={formatNumber}
              selectedCampaignFilter={campagneFiltre}
              // Un second clic sur la même ligne lève le filtre : c'est le geste qu'on tente
              // naturellement, et il n'y a pas d'autre sortie dans ce tableau.
              setSelectedCampaignFilter={(c) => setCampagneFiltre(
                actuel => (actuel && actuel.id === c?.id ? null : c)
              )}
              selectedCampaignTypeFilter={null}
              selectedAccount={compteSelectionne}
              compareEnabled={compareEnabled}
              comparisonType={comparisonType}
              userRole={userRole}
              isClientModuleVisible={isModuleVisible}
              toggleClientModuleVisibility={toggleModuleVisibility}
              moduleId="awin_campaigns"
              titre="Campagnes"
              titreColonne="Campagne"
              campagnesAccountId={compteSelectionne?.sourceId || compteSelectionne?.id}
              campagnesAccountType="AWIN"
              campagnesToken={token}
            />
          )}

          {/* La maturité des commissions, que le tableau ci-dessus ne peut pas montrer :
              il dit les montants, celui-ci dit ce qui est acquis. */}
          <AwinCommissionStatus
            editeurs={donnees.parEditeur}
            userRole={userRole}
            isClientModuleVisible={isModuleVisible}
            toggleClientModuleVisibility={toggleModuleVisibility}
          />
        </>
      )}
    </div>
  );
};

export default AwinDashboardView;
